import type { PostPicklistItemRequest } from '../../types';
import type { PostRow } from './postRows';

/**
 * PT-5 C2 — the claim request, in TODAY'S shape: one `items` entry per selected row under the planned line's id
 * (= `PicklistItemId`), `itemMemos` and `itemReadings` keyed by it. No request v2: under the derived model a row
 * is one line, so today's shape already says everything a row posts. Pure; `postPayload.test.ts` pins it.
 */
export interface PostPayloadInput {
  rows: readonly PostRow[];
  selected: ReadonlySet<number>;
  /** Non-serial rows' To post, already parsed. */
  quantities: Readonly<Record<number, number>>;
  /** Serial slots ticked in the sheet (`picklistItemBinId`). */
  ticked: ReadonlySet<number>;
  memos: Readonly<Record<number, string>>;
  /** One reading per ITEM (anchor id): a number, or null for "send none". */
  readings: Readonly<Record<number, number | null>>;
}

export interface PostPayload {
  items: PostPicklistItemRequest[];
  itemMemos?: Record<number, string>;
  itemReadings: Record<number, number | null>;
}

/** What a row sends as its quantity: the ticked serials' count, or the typed To post. */
export function rowQuantity(row: PostRow, quantities: Readonly<Record<number, number>>, ticked: ReadonlySet<number>): number {
  return row.isSerial
    ? row.serials.filter((u) => u.available && ticked.has(u.slotId)).length
    : quantities[row.id] ?? row.max;
}

export function buildPostPayload({ rows, selected, quantities, ticked, memos, readings }: PostPayloadInput): PostPayload {
  const items: PostPicklistItemRequest[] = [];
  const itemMemos: Record<number, string> = {};
  const itemReadings: Record<number, number | null> = {};

  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    const q = rowQuantity(row, quantities, ticked);
    // A selected row at 0 is left out, silently (O-20 slot 16): Post is disabled only when every selected row is 0.
    if (!(q > 0)) continue;

    items.push(row.isSerial
      ? {
          picklistItemId: row.id,
          quantityToPost: q,
          // Every tickable slot, with its DoIssue: the server asserts each serial against its slot (PW-05) and
          // refuses a slot an open batch holds (PT-5 C5). Held and posted slots are not tickable, so not sent.
          unitsToIssue: row.serials
            .filter((u) => u.available)
            .map((u) => ({ picklistItemBinId: u.slotId, inventoryNumberId: u.inventoryNumberId, doIssue: ticked.has(u.slotId) })),
        }
      : { picklistItemId: row.id, quantityToPost: q });

    const memo = (memos[row.id] ?? '').trim();
    if (memo) itemMemos[row.id] = memo;

    // C6 "Reading": the item's one value under EVERY selected row's id — a number or an explicit null, never key-
    // absent — so each NetSuite line of the item resolves the same value, whichever of its lines it holds.
    itemReadings[row.id] = readings[row.anchorId] ?? null;
  }

  return { items, itemMemos: Object.keys(itemMemos).length > 0 ? itemMemos : undefined, itemReadings };
}
